import { getMapByMissionId, DEFAULT_MAP_ID } from './maps/index.js';
import { SPATIAL_SCALE } from '../../shared/spatial.js';
import { sampleMapHeight, isPointInsideMapBounds } from '../../shared/maps/terrainSampling.js';
import type { GameSnapshot, ResourceNodeState, RoomState } from '../../shared/types.js';
import type { MapDefinition } from '../../shared/maps/types.js';
import type { AggregatedConnection, TileJsonNode } from '../../shared/maps/tileJson.js';
import {
  SECTION_AXES, clipSectionToRect, heightToDepth, localYToDepth, makeSectionFrame, niceTickStep, projectToSection,
  sampleSeabedProfile, subFacingSign, tileBandsAlongSection, unprojectFromSection, zoneSectionSpan,
  type NavGatewayEntry, type SectionAxis, type SectionFrame, type TileBand, type TileFootprint,
  enclosedTileLayerKeys, isEnclosedTileKind
} from './navSection.js';
import { nodeHalfY, nodeSectionSpan, nodeShapeOf, isBoxNode } from '../../shared/maps/navShapes.js';
import { buildRevealGraph, parseDiscs, planPassageDraw, type Disc, type PassageDrawPlan, type RevealGraph } from '../../shared/maps/navReveal.js';

// ---------------------------------------------------------------------------
// Side (elevation) view of the navigation room.
//
// A vertical section through the world along a horizontal axis (sub heading by default): depth on the vertical axis,
// distance along the section line on the horizontal one. Drawn from the same data as the plan view: tile companion
// JSON nodes / passages / gateways (never GLB models) plus the height samples already in the client map.
// One canvas, redrawn on a throttle (data changes) or at animation rate while the finger is down.
// ---------------------------------------------------------------------------

const BG = '#050810';
const SURFACE_BG = '#0b2230';
const NODE_FILL = '#2a7a7a';
const NODE_RING = '#3db8a8';
const NODE_LABEL = '#6aaba0';
const CONN_STROKE = '#2a6478';
const SUB_COLOUR = '#00ffe0';
const WAYPOINT_COLOUR = '#e8b84b';
const MARKER_COLOUR = '#e06060';
const SELECT_COLOUR = '#dc3c3c';
const SHAFT_COLOUR = '#ffb347';
const ROCK_FILL = 'rgba(70,58,48,0.42)';
/** Alpha multiplier for node/route graphics in open-water tiles (they are hints, not tunnels). */
const OPEN_WATER_ROUTE_ALPHA = 0.4;
const FLOOR_FILL = '#101c26';
const FLOOR_STROKE = '#4a7f8f';
const AXIS_COLOUR = '#6f98a6';
const GRID_COLOUR = 'rgba(111,152,166,0.10)';

const REDRAW_INTERVAL_MS = 80;
const CLICK_MOVE_THRESHOLD_PX = 6;
const GRAB_RADIUS_PX = 24;
const MIN_SCALE = 0.35;
const MAX_SCALE = 14;
const AXIS_GUTTER_PX = 38;
/** Objects farther than this from the section plane are drawn at the minimum opacity. */
const LATERAL_FADE_METERS = 160;
/** Within this lateral offset an object is treated as lying on the section plane. */
const LATERAL_FULL_METERS = 24;
const STORAGE_KEY = 'aegis7.nav.sideAxis';

type NodeEntry = { node: TileJsonNode; tileId: string; chunkRegion: string; biome: string; tempC: number; tileKind?: string };

/** What the side view needs from its owner (the 2.5D wrapper delegates to the plan view). */
export interface SideViewLink {
  getWaypoint(): { x: number; z: number; depth?: number } | null;
  getMarker(): { x: number; z: number; depth?: number } | null;
  /** The player tapped / dragged the tap marker (map space + depth), or removed it (null). */
  setMarker(marker: { x: number; z: number; depth: number } | null): void;
  /** The player finished dragging the shared waypoint. */
  moveWaypoint(x: number, z: number, depth: number): void;
}

type Mark = { x: number; z: number; depth?: number };

const clamp = (v: number, lo: number, hi: number): number => Math.max(lo, Math.min(hi, v));

export class CaptainNavigationSideView {
  private readonly container: HTMLElement;
  private readonly canvas: HTMLCanvasElement;
  private readonly link: SideViewLink;
  private readonly resizeObserver: ResizeObserver;

  // Data
  private nodeEntries: NodeEntry[] = [];
  private nodeByKey = new Map<string, NodeEntry>();
  private connections: AggregatedConnection[] = [];
  private gateways: NavGatewayEntry[] = [];
  private revealedNodeIds: Set<string> = new Set();
  /** Swept discs decoded from the reveal state, the passage centre lines, and what to draw of each (see navReveal.ts). */
  private discs: Disc[] = [];
  private graph: RevealGraph = { nodes: new Map(), connections: [], lines: [] };
  private plans: PassageDrawPlan[] = [];
  private activeMap: MapDefinition | null = null;
  private tileFootprints: TileFootprint[] = [];
  /** Tile layers that are enclosed cave tiles (see navSection ENCLOSED_TILE_KINDS): no seabed silhouette is drawn in them. */
  private enclosedLayerKeys: Set<string> = new Set();
  private dockingPorts: Array<{ x: number; z: number; depth: number; label: string }> = [];
  private deepestMeters = 240;
  private missionTarget: { x: number; z: number; depth?: number } | null = null;
  private contacts: ResourceNodeState[] = [];

  // Submarine
  private subX = 0;
  private subZ = 0;
  private subDepth = 100;
  private subYaw = 0;
  private subPitch = 0;
  private haveSnapshot = false;

  // View
  private axis: SectionAxis = 'heading';
  private panS = 0;
  private panDepth = 100;
  private scale = 1.5;
  private hasPanned = false;
  private initialFitDone = false;
  private frame: SectionFrame = makeSectionFrame('heading', 0, 0, 0);

  // Drawing scratch (reused: no per-frame allocation storms)
  private profile: Float32Array = new Float32Array(256);
  private cssW = 0;
  private cssH = 0;
  private pixelRatio = 1;
  private dirty = true;
  private lastDrawMs = 0;
  private rafId = 0;
  private animating = false;

  // Pointer state
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private lastPinchDist = 0;
  private downX = 0;
  private downY = 0;
  private moved = false;
  private dragTarget: 'waypoint' | 'marker' | null = null;
  private dragMark: { x: number; z: number; depth: number; lat: number } | null = null;

  private readonly axisButtons = new Map<SectionAxis, HTMLButtonElement>();

  constructor(container: HTMLElement, link: SideViewLink) {
    this.container = container;
    this.link = link;
    container.style.position = 'relative';
    container.style.overflow = 'hidden';
    container.style.background = BG;

    this.canvas = document.createElement('canvas');
    this.canvas.style.cssText = 'position:absolute;top:0;left:0;width:100%;height:100%;touch-action:none;';
    container.appendChild(this.canvas);

    this.axis = this.loadAxis();
    container.appendChild(this.buildAxisSelector());

    this.canvas.addEventListener('pointerdown', this.onPointerDown);
    this.canvas.addEventListener('pointermove', this.onPointerMove);
    this.canvas.addEventListener('pointerup', this.onPointerUp);
    this.canvas.addEventListener('pointercancel', this.onPointerUp);
    this.canvas.addEventListener('wheel', this.onWheel, { passive: false });

    this.resizeObserver = new ResizeObserver(() => this.syncSize());
    this.resizeObserver.observe(container);
    this.syncSize();
    this.rafId = requestAnimationFrame(this.loop);
  }

  // -------------------------------------------------------------------------
  // Public API (mirrors the plan view)
  // -------------------------------------------------------------------------

  public setTileData(nodeEntries: NodeEntry[], connections: AggregatedConnection[], gateways: NavGatewayEntry[] = []): void {
    this.nodeEntries = nodeEntries;
    this.nodeByKey = new Map(nodeEntries.map((e) => [`${e.tileId}:${e.node.id}`, e]));
    this.connections = connections;
    this.gateways = gateways;
    this.graph = buildRevealGraph(nodeEntries, connections);
    this.replanPassages();
    this.refreshEnclosedLayers();
    this.dirty = true;
  }

  private refreshEnclosedLayers(): void {
    const map = this.activeMap;
    const tileY = map?.sourceGlbTileSizeMeters?.Y ?? 0;
    if (!map || tileY <= 0) { this.enclosedLayerKeys = new Set(); return; }
    const nodes = this.nodeEntries.map((e) => ({ x: e.node.x, z: e.node.z, depth: this.nodeDepth(e.node), enclosed: isEnclosedTileKind(e.tileKind) }));
    this.enclosedLayerKeys = enclosedTileLayerKeys(this.tileFootprints, nodes, tileY, map.baseDepth);
  }

  public setRevealedNodes(ids: Set<string>): void {
    this.revealedNodeIds = ids;
    this.replanPassages();
    this.dirty = true;
  }

  private replanPassages(): void {
    this.discs = parseDiscs(this.revealedNodeIds);
    this.plans = this.connections.map((conn, i) =>
      planPassageDraw(this.graph.lines[i] ?? [], this.discs, this.revealedNodeIds.has(conn.fromKey), !conn.deadEnd && this.revealedNodeIds.has(conn.toKey), !!conn.deadEnd));
  }

  public setRoomState(state: Pick<RoomState, 'mission' | 'missionObjectives' | 'activeMissionIndex'>): void {
    const map = getMapByMissionId(state.mission) ?? getMapByMissionId(DEFAULT_MAP_ID) ?? null;
    if (map !== this.activeMap) {
      this.activeMap = map;
      this.cacheMapData(map);
      this.refreshEnclosedLayers();
    }
    const objective = state.missionObjectives[state.activeMissionIndex] ?? null;
    this.missionTarget = objective?.location ?? null;
    this.dirty = true;
  }

  public setSnapshot(snapshot: GameSnapshot): void {
    this.subX = snapshot.submarine.simulationX * SPATIAL_SCALE.mapXPerSimulationX;
    this.subZ = snapshot.submarine.simulationZ * SPATIAL_SCALE.mapZPerSimulationY;
    this.subDepth = snapshot.submarine.depth;
    this.subYaw = snapshot.submarine.yaw;
    this.subPitch = snapshot.submarine.pitch ?? 0;
    this.contacts = (snapshot.world?.resourceNodes ?? []).filter((node) => node.discovered);
    if (!this.haveSnapshot) {
      this.haveSnapshot = true;
      this.panDepth = this.subDepth;
    }
    if (!this.hasPanned) this.panDepth = this.subDepth;
    this.dirty = true;
  }

  public markDirty(): void {
    this.dirty = true;
  }

  public getViewState(): { axis: SectionAxis; scale: number; panS: number; panDepth: number; hasPanned: boolean } {
    return { axis: this.axis, scale: this.scale, panS: this.panS, panDepth: this.panDepth, hasPanned: this.hasPanned };
  }

  public restoreViewState(state: { axis: SectionAxis; scale: number; panS: number; panDepth: number; hasPanned: boolean }): void {
    this.setAxis(state.axis, false);
    this.scale = state.scale;
    this.panS = state.panS;
    this.panDepth = state.panDepth;
    this.hasPanned = state.hasPanned;
    this.initialFitDone = true;
    this.dirty = true;
  }

  public recenter(): void {
    this.hasPanned = false;
    this.panS = 0;
    this.panDepth = this.subDepth;
    this.scale = this.defaultScale();
    this.dirty = true;
  }

  public setAxis(axis: SectionAxis, persist = true): void {
    this.axis = axis;
    for (const [a, button] of this.axisButtons) button.classList.toggle('active', a === axis);
    if (persist) {
      try { localStorage.setItem(STORAGE_KEY, axis); } catch { /* storage unavailable */ }
    }
    this.dirty = true;
  }

  public dispose(): void {
    cancelAnimationFrame(this.rafId);
    this.resizeObserver.disconnect();
    this.canvas.removeEventListener('pointerdown', this.onPointerDown);
    this.canvas.removeEventListener('pointermove', this.onPointerMove);
    this.canvas.removeEventListener('pointerup', this.onPointerUp);
    this.canvas.removeEventListener('pointercancel', this.onPointerUp);
    this.canvas.removeEventListener('wheel', this.onWheel);
  }

  // -------------------------------------------------------------------------
  // Setup helpers
  // -------------------------------------------------------------------------

  private loadAxis(): SectionAxis {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored && (SECTION_AXES as readonly string[]).includes(stored)) return stored as SectionAxis;
    } catch { /* storage unavailable */ }
    return 'heading';
  }

  private buildAxisSelector(): HTMLElement {
    const bar = document.createElement('div');
    bar.className = 'nav-side-axis';
    const label = document.createElement('span');
    label.className = 'nav-side-axis-label';
    label.textContent = 'SECTION';
    bar.appendChild(label);
    const names: Record<SectionAxis, string> = { heading: 'HDG', ns: 'N-S', ew: 'E-W' };
    for (const axis of SECTION_AXES) {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = names[axis];
      button.title = axis === 'heading' ? 'Section along the submarine heading' : axis === 'ns' ? 'Section north-south' : 'Section east-west';
      button.classList.toggle('active', axis === this.axis);
      button.addEventListener('click', (event) => { event.stopPropagation(); this.setAxis(axis); });
      button.addEventListener('pointerdown', (event) => event.stopPropagation());
      this.axisButtons.set(axis, button);
      bar.appendChild(button);
    }
    return bar;
  }

  private cacheMapData(map: MapDefinition | null): void {
    this.tileFootprints = [];
    this.dockingPorts = [];
    this.deepestMeters = 240;
    if (!map) return;
    for (const entry of map.tileIndex ?? []) {
      this.tileFootprints.push({ key: entry.key, tileY: entry.tileY, minX: entry.minX, maxX: entry.maxX, minZ: entry.minZ, maxZ: entry.maxZ, originY: entry.originY });
    }
    const tileY = map.sourceGlbTileSizeMeters?.Y ?? 0;
    for (const tile of this.tileFootprints) {
      const bottom = -(map.baseDepth + (tile.originY ?? tile.tileY * tileY) - tileY / 2);
      this.deepestMeters = Math.max(this.deepestMeters, bottom + 20);
    }
    for (const marker of map.glbModelMarkers ?? []) {
      if (marker.model.toLowerCase() === 'docking_port') {
        this.dockingPorts.push({ x: marker.mapX, z: marker.mapZ, depth: marker.depth, label: 'DOCK' });
      }
    }
  }

  private syncSize(): void {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    const ratio = Math.min(typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1, 2);
    if (w === this.cssW && h === this.cssH && ratio === this.pixelRatio) return;
    this.cssW = w;
    this.cssH = h;
    this.pixelRatio = ratio;
    if (w > 0 && h > 0) {
      this.canvas.width = Math.round(w * ratio);
      this.canvas.height = Math.round(h * ratio);
      if (!this.initialFitDone) {
        this.scale = this.defaultScale();
        this.initialFitDone = true;
      }
    }
    this.dirty = true;
  }

  private defaultScale(): number {
    const w = this.cssW || 400;
    const h = this.cssH || 300;
    return clamp(Math.min(w / 260, h / 170), MIN_SCALE, MAX_SCALE);
  }

  // -------------------------------------------------------------------------
  // Coordinates
  // -------------------------------------------------------------------------

  private sToPx(s: number): number {
    return this.cssW / 2 + (s - this.panS) * this.scale;
  }

  private depthToPy(depth: number): number {
    return this.cssH / 2 + (depth - this.panDepth) * this.scale;
  }

  private pxToS(px: number): number {
    return (px - this.cssW / 2) / this.scale + this.panS;
  }

  private pyToDepth(py: number): number {
    return (py - this.cssH / 2) / this.scale + this.panDepth;
  }

  private baseDepth(): number {
    return this.activeMap?.baseDepth ?? 0;
  }

  private nodeDepth(node: TileJsonNode): number {
    return localYToDepth(node.y, 0, this.baseDepth());
  }

  /** 1 on the section plane, fading toward 0.18 for things far to either side of it. */
  private lateralAlpha(lat: number): number {
    const d = Math.abs(lat);
    if (d <= LATERAL_FULL_METERS) return 1;
    return clamp(1 - (d - LATERAL_FULL_METERS) / LATERAL_FADE_METERS, 0.18, 1);
  }

  // -------------------------------------------------------------------------
  // Render loop
  // -------------------------------------------------------------------------

  private readonly loop = (now: number): void => {
    this.rafId = requestAnimationFrame(this.loop);
    if (this.cssW <= 0 || this.cssH <= 0) return; // hidden pane
    if (typeof document !== 'undefined' && document.hidden) return;
    const interval = this.pointers.size > 0 ? 0 : REDRAW_INTERVAL_MS;
    if (now - this.lastDrawMs < interval) return;
    if (!this.dirty && !this.animating) return;
    this.lastDrawMs = now;
    this.dirty = false;
    this.draw();
  };

  private draw(): void {
    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;
    const W = this.cssW;
    const H = this.cssH;
    ctx.setTransform(this.pixelRatio, 0, 0, this.pixelRatio, 0, 0);

    this.frame = makeSectionFrame(this.axis, this.subX, this.subZ, this.subYaw);
    const sMin = this.pxToS(0);
    const sMax = this.pxToS(W);
    const depthMin = this.pyToDepth(0);
    const depthMax = this.pyToDepth(H);
    this.animating = false;

    ctx.fillStyle = BG;
    ctx.fillRect(0, 0, W, H);
    // Open water above the surface line
    const surfaceY = this.depthToPy(0);
    if (surfaceY > 0) {
      ctx.fillStyle = SURFACE_BG;
      ctx.fillRect(0, 0, W, Math.min(surfaceY, H));
    }

    this.drawGrid(ctx, W, H, depthMin, depthMax, sMin, sMax);
    this.drawZones(ctx, sMin, sMax, depthMin, depthMax);
    const bands = this.drawTileBands(ctx, sMin, sMax);
    this.drawSeabed(ctx, bands, sMin, sMax);
    this.drawPassages(ctx, sMin, sMax);
    this.drawNodes(ctx, sMin, sMax);
    this.drawGateways(ctx, sMin, sMax);
    this.drawDockingPorts(ctx, sMin, sMax);
    this.drawContacts(ctx, sMin, sMax);
    this.drawMissionTarget(ctx);
    this.drawSelection(ctx);
    this.drawSubmarine(ctx);
    this.drawAxes(ctx, W, H, depthMin, depthMax);
    this.drawReadout(ctx, W);
  }

  // ---- background ---------------------------------------------------------

  private drawGrid(ctx: CanvasRenderingContext2D, W: number, H: number, depthMin: number, depthMax: number, sMin: number, sMax: number): void {
    const step = niceTickStep(this.scale);
    ctx.strokeStyle = GRID_COLOUR;
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (let d = Math.ceil(depthMin / step) * step; d <= depthMax; d += step) {
      const y = Math.round(this.depthToPy(d)) + 0.5;
      ctx.moveTo(AXIS_GUTTER_PX, y);
      ctx.lineTo(W, y);
    }
    for (let s = Math.ceil(sMin / step) * step; s <= sMax; s += step) {
      const x = Math.round(this.sToPx(s)) + 0.5;
      ctx.moveTo(x, 0);
      ctx.lineTo(x, H);
    }
    ctx.stroke();
    // The surface
    const sy = this.depthToPy(0);
    if (sy >= 0 && sy <= H) {
      ctx.strokeStyle = 'rgba(120,200,230,0.55)';
      ctx.lineWidth = 1.5;
      ctx.beginPath();
      ctx.moveTo(0, sy);
      ctx.lineTo(W, sy);
      ctx.stroke();
      ctx.fillStyle = 'rgba(120,200,230,0.7)';
      ctx.font = '9px monospace';
      ctx.textAlign = 'right';
      ctx.fillText('SURFACE', W - 6, sy - 4);
    }
  }

  private drawZones(ctx: CanvasRenderingContext2D, sMin: number, sMax: number, depthMin: number, depthMax: number): void {
    const map = this.activeMap;
    if (!map) return;
    const ambient = map.missionLayout?.waterTemperature ?? 0;
    const drawZone = (
      zone: { x: number; z: number; radius: number; halfWidth?: number; halfDepth?: number; depthMin?: number; depthMax?: number },
      fill: string,
      label: string
    ): void => {
      const span = zoneSectionSpan(zone, this.frame, sMin, sMax);
      if (!span) return;
      const top = Math.max(zone.depthMin ?? 0, depthMin);
      const bottom = Math.min(zone.depthMax !== undefined && zone.depthMax < 1e6 ? zone.depthMax : depthMax, depthMax);
      if (bottom <= top) return;
      const x0 = this.sToPx(span.s0);
      const x1 = this.sToPx(span.s1);
      const y0 = this.depthToPy(top);
      const y1 = this.depthToPy(bottom);
      ctx.fillStyle = fill;
      ctx.fillRect(x0, y0, x1 - x0, y1 - y0);
      ctx.fillStyle = 'rgba(200,225,235,0.7)';
      ctx.font = '9px monospace';
      ctx.textAlign = 'left';
      ctx.fillText(label, Math.max(x0, AXIS_GUTTER_PX) + 3, Math.max(y0, 0) + 11);
    };
    for (const zone of map.currentZones ?? []) {
      drawZone(zone, 'rgba(70,170,230,0.10)', `CURRENT ${zone.strength.toFixed(1)}`);
    }
    for (const zone of map.thermalZones ?? []) {
      const warm = zone.temperatureC > ambient;
      drawZone(zone, warm ? 'rgba(235,120,50,0.13)' : 'rgba(110,150,255,0.13)', `${warm ? 'WARM' : 'COLD'} ${Math.round(zone.temperatureC)}°C`);
    }
    for (const zone of map.visibilityZones ?? []) {
      drawZone(zone, 'rgba(210,220,225,0.08)', `VIS ${Math.round(zone.visibilityMeters)} m`);
    }
  }

  /**
   * The vertical extent of each tile layer the section crosses. Used to bound the seabed silhouette only: tile
   * boundaries and layer labels are deliberately NOT drawn (players should not see the map's tiling).
   */
  private drawTileBands(_ctx: CanvasRenderingContext2D, sMin: number, sMax: number): TileBand[] {
    const map = this.activeMap;
    const tileY = map?.sourceGlbTileSizeMeters?.Y ?? 0;
    if (!map || tileY <= 0 || this.tileFootprints.length === 0) return [];
    return tileBandsAlongSection(this.tileFootprints, tileY, map.baseDepth, this.frame, sMin, sMax);
  }

  private drawSeabed(ctx: CanvasRenderingContext2D, bands: TileBand[], sMin: number, sMax: number): void {
    const map = this.activeMap;
    if (!map) return;
    const hasTiles = this.tileFootprints.length > 0 && bands.length > 0;
    const heightAt = (x: number, z: number): number => sampleMapHeight(map, x, z);

    const drawRange = (s0: number, s1: number, floorLimitDepth: number | null): void => {
      const a = Math.max(s0, sMin);
      const b = Math.min(s1, sMax);
      if (b - a < 0.5) return;
      const count = clamp(Math.ceil(((b - a) * this.scale) / 3) + 1, 2, 220);
      this.profile = sampleSeabedProfile(heightAt, map.baseDepth, this.frame, a, b, count, this.profile);
      const step = (b - a) / (count - 1);
      ctx.beginPath();
      let open = false;
      let firstX = 0;
      let lastX = 0;
      // Fill down to the tile's own bottom (so stacked layers below stay visible), or the canvas bottom without tiles.
      const fillBottom = floorLimitDepth !== null ? Math.min(this.depthToPy(floorLimitDepth), this.cssH + 4) : this.cssH + 4;
      const flush = (): void => {
        if (!open) return;
        ctx.lineTo(lastX, fillBottom);
        ctx.lineTo(firstX, fillBottom);
        ctx.closePath();
        open = false;
      };
      for (let i = 0; i < count; i += 1) {
        const depth = this.profile[i];
        if (!Number.isFinite(depth) || (floorLimitDepth !== null && depth > floorLimitDepth + 1)) {
          flush();
          continue;
        }
        const x = this.sToPx(a + step * i);
        const y = this.depthToPy(depth);
        if (!open) { ctx.moveTo(x, y); firstX = x; open = true; } else ctx.lineTo(x, y);
        lastX = x;
      }
      flush();
      ctx.fillStyle = FLOOR_FILL;
      ctx.fill();
      ctx.strokeStyle = FLOOR_STROKE;
      ctx.lineWidth = 1.5;
      // Stroke only the floor line, not the closing edges
      ctx.save();
      ctx.beginPath();
      let pen = false;
      for (let i = 0; i < count; i += 1) {
        const depth = this.profile[i];
        if (!Number.isFinite(depth) || (floorLimitDepth !== null && depth > floorLimitDepth + 1)) { pen = false; continue; }
        const x = this.sToPx(a + step * i);
        const y = this.depthToPy(depth);
        if (!pen) { ctx.moveTo(x, y); pen = true; } else ctx.lineTo(x, y);
      }
      ctx.stroke();
      ctx.restore();
    };

    if (hasTiles) {
      // Heights are merged per footprint (the shallowest stacked layer wins), so draw each footprint once.
      const topmost = new Map<string, TileBand>();
      for (const band of bands) {
        const tile = this.tileFootprints.find((t) => t.key === band.key);
        if (!tile) continue;
        const footprint = `${tile.minX},${tile.minZ}`;
        // Enclosed cave tiles have no seabed: their passages are the picture (see navSection rule).
        if (this.enclosedLayerKeys.has(band.key)) {
          // Solid rock behind the tunnels, so the passages read as bores through it rather than floating in open water.
          ctx.fillStyle = ROCK_FILL;
          const rx0 = this.sToPx(band.s0);
          const ry0 = this.depthToPy(band.depthTop);
          ctx.fillRect(rx0, ry0, this.sToPx(band.s1) - rx0, this.depthToPy(band.depthBottom) - ry0);
          continue;
        }
        const prior = topmost.get(footprint);
        if (!prior || band.depthTop < prior.depthTop) topmost.set(footprint, band);
      }
      for (const band of topmost.values()) drawRange(band.s0, band.s1, band.depthBottom);
    } else if (this.tileFootprints.length === 0) {
      // Single-GLB map: one dense grid; blank outside the map bounds.
      this.profileInsideBounds(map, sMin, sMax, drawRange);
    }
  }

  /** For maps without a tile index: draw the floor only where the section line is inside the map bounds. */
  private profileInsideBounds(map: MapDefinition, sMin: number, sMax: number, drawRange: (s0: number, s1: number, limit: number | null) => void): void {
    const span = clipSectionToRect(this.frame, sMin, sMax, -map.sizeMeters.width / 2, map.sizeMeters.width / 2, -map.sizeMeters.height / 2, map.sizeMeters.height / 2);
    if (span && isPointInsideMapBounds(map, this.frame.originX, this.frame.originZ, 1e6)) drawRange(span.s0, span.s1, null);
  }

  // ---- passages & nodes ---------------------------------------------------

  private drawPassages(ctx: CanvasRenderingContext2D, sMin: number, sMax: number): void {
    const baseDepth = this.baseDepth();
    ctx.save();
    ctx.lineJoin = 'round';
    this.connections.forEach((conn, i) => {
      const plan = this.plans[i];
      if (!plan || (plan.solid.length === 0 && plan.stub.length === 0)) return;
      const fromEntry = this.nodeByKey.get(conn.fromKey);
      const toEntry = conn.deadEnd ? undefined : this.nodeByKey.get(conn.toKey);
      // Routes through open water are only a faint hint; real tunnels (enclosed tiles) are drawn solid.
      const inEnclosed = isEnclosedTileKind(fromEntry?.tileKind) || (!!toEntry && isEnclosedTileKind(toEntry.tileKind));
      const widthPx = Math.max(conn.width * this.scale, 2);
      // Round tubes end in round caps, box corridors in square ends.
      ctx.lineCap = conn.shape === 'box' ? 'butt' : 'round';
      const drawPiece = (points: Array<[number, number, number]>, strength: number): void => {
        // Cull on the s range and work out how close the passage runs to the section plane.
        let minS = Infinity;
        let maxS = -Infinity;
        let minLat = Infinity;
        for (const p of points) {
          const pr = projectToSection(this.frame, p[0], p[2]);
          if (pr.s < minS) minS = pr.s;
          if (pr.s > maxS) maxS = pr.s;
          if (Math.abs(pr.lat) < minLat) minLat = Math.abs(pr.lat);
        }
        if (maxS < sMin - 20 || minS > sMax + 20) return;
        const alpha = this.lateralAlpha(minLat) * strength * (inEnclosed ? 1 : OPEN_WATER_ROUTE_ALPHA);
        ctx.globalAlpha = alpha * 0.55;
        ctx.strokeStyle = CONN_STROKE;
        ctx.lineWidth = widthPx;
        this.tracePassage(ctx, points, baseDepth);
        ctx.stroke();
        ctx.globalAlpha = alpha;
        ctx.strokeStyle = 'rgba(100,190,200,0.55)';
        ctx.lineWidth = 1;
        this.tracePassage(ctx, points, baseDepth);
        ctx.stroke();
      };
      for (const piece of plan.solid) if (piece.length >= 2) drawPiece(piece, plan.complete ? 1 : 0.8);
      if (plan.stub.length >= 2) drawPiece(plan.stub, 0.45);
    });
    ctx.restore();
  }

  private tracePassage(ctx: CanvasRenderingContext2D, points: Array<[number, number, number]>, baseDepth: number): void {
    ctx.beginPath();
    for (let i = 0; i < points.length; i += 1) {
      const pr = projectToSection(this.frame, points[i][0], points[i][2]);
      const x = this.sToPx(pr.s);
      const y = this.depthToPy(localYToDepth(points[i][1], 0, baseDepth));
      if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
    }
  }

  private drawNodes(ctx: CanvasRenderingContext2D, sMin: number, sMax: number): void {
    ctx.save();
    ctx.font = '10px monospace';
    ctx.textAlign = 'center';
    for (const entry of this.nodeEntries) {
      if (!this.revealedNodeIds.has(`${entry.tileId}:${entry.node.id}`)) continue;
      const node = entry.node;
      const pr = projectToSection(this.frame, node.x, node.z);
      if (pr.s + node.radius_xz < sMin || pr.s - node.radius_xz > sMax) continue;
      // Where the plane cuts the node's footprint (exact for rotated boxes, a chord for round caves and tubes);
      // off-plane nodes show as a smaller ghost.
      const span = nodeSectionSpan(node, this.frame);
      const lat = Math.abs(pr.lat);
      const onPlane = span !== null;
      const rx = Math.max((onPlane ? (span.s1 - span.s0) / 2 : node.radius_xz * 0.35) * this.scale, 3);
      const ry = Math.max(nodeHalfY(node) * this.scale, 3);
      const cx = this.sToPx(onPlane ? (span.s0 + span.s1) / 2 : pr.s);
      const cy = this.depthToPy(this.nodeDepth(node));
      const alpha = onPlane ? 1 : this.lateralAlpha(lat - node.radius_xz);
      ctx.beginPath();
      // Box chambers and shafts are rectangles in section; round caves are ellipses.
      if (isBoxNode(node) || nodeShapeOf(node) === 'tube') ctx.rect(cx - rx, cy - ry, rx * 2, ry * 2);
      else ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
      ctx.fillStyle = NODE_FILL;
      ctx.globalAlpha = alpha * 0.55;
      ctx.fill();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = NODE_RING;
      ctx.lineWidth = 1.2;
      ctx.stroke();
      if (rx > 22 && ry > 8) {
        ctx.fillStyle = NODE_LABEL;
        ctx.fillText(node.node_name ?? node.name, cx, cy + 3);
      }
    }
    ctx.restore();
  }

  private drawGateways(ctx: CanvasRenderingContext2D, sMin: number, sMax: number): void {
    const baseDepth = this.baseDepth();
    const pulse = 0.65 + 0.35 * Math.sin(performance.now() / 420);
    ctx.save();
    ctx.font = 'bold 9px monospace';
    ctx.textAlign = 'center';
    for (const gw of this.gateways) {
      if (!gw.nodeKeys.some((k) => this.revealedNodeIds.has(k))) continue;
      const vertical = gw.side === '+y' || gw.side === '-y';
      const pr = projectToSection(this.frame, gw.x, gw.z);
      if (pr.s < sMin - 10 || pr.s > sMax + 10) continue;
      const alpha = this.lateralAlpha(pr.lat);
      const cx = this.sToPx(pr.s);
      const cy = this.depthToPy(localYToDepth(gw.y, 0, baseDepth));
      ctx.globalAlpha = alpha;
      if (vertical) {
        this.animating = true;
        const up = gw.side === '+y';
        const half = Math.max(gw.radius * this.scale, 9);
        // Shaft: glowing column with chevrons pointing the way (up = toward the surface).
        const grad = ctx.createLinearGradient(0, cy - half * 2, 0, cy + half * 2);
        grad.addColorStop(0, up ? 'rgba(255,179,71,0.55)' : 'rgba(255,179,71,0)');
        grad.addColorStop(1, up ? 'rgba(255,179,71,0)' : 'rgba(255,179,71,0.55)');
        ctx.fillStyle = grad;
        ctx.fillRect(cx - half, cy - half * 2, half * 2, half * 4);
        ctx.strokeStyle = SHAFT_COLOUR;
        ctx.lineWidth = 2;
        ctx.globalAlpha = alpha * (0.6 + 0.4 * pulse);
        const dir = up ? -1 : 1;
        for (let i = 0; i < 2; i += 1) {
          const y = cy + dir * (i * 8 - 4);
          ctx.beginPath();
          ctx.moveTo(cx - 7, y - dir * 4);
          ctx.lineTo(cx, y + dir * 4);
          ctx.lineTo(cx + 7, y - dir * 4);
          ctx.stroke();
        }
        ctx.globalAlpha = alpha;
        ctx.fillStyle = SHAFT_COLOUR;
        ctx.fillText(up ? 'SHAFT ▲ UP' : 'SHAFT ▼ DOWN', cx, cy + (up ? half * 2 + 11 : -half * 2 - 5));
      } else {
        ctx.fillStyle = 'rgba(61,184,168,0.85)';
        ctx.fillRect(cx - 2, cy - Math.max(gw.radius * this.scale, 4), 4, Math.max(gw.radius * this.scale, 4) * 2);
      }
    }
    ctx.restore();
  }

  private drawDockingPorts(ctx: CanvasRenderingContext2D, sMin: number, sMax: number): void {
    if (this.dockingPorts.length === 0) return;
    ctx.save();
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    for (const port of this.dockingPorts) {
      const pr = projectToSection(this.frame, port.x, port.z);
      if (pr.s < sMin - 10 || pr.s > sMax + 10) continue;
      const cx = this.sToPx(pr.s);
      const cy = this.depthToPy(port.depth);
      ctx.globalAlpha = this.lateralAlpha(pr.lat);
      ctx.fillStyle = '#7fe4f2';
      ctx.beginPath();
      ctx.moveTo(cx, cy - 6);
      ctx.lineTo(cx + 6, cy + 5);
      ctx.lineTo(cx - 6, cy + 5);
      ctx.closePath();
      ctx.fill();
      ctx.fillText(port.label, cx, cy + 16);
    }
    ctx.restore();
  }

  private drawContacts(ctx: CanvasRenderingContext2D, sMin: number, sMax: number): void {
    if (this.contacts.length === 0) return;
    ctx.save();
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    for (const node of this.contacts) {
      const x = node.simulationX * SPATIAL_SCALE.mapXPerSimulationX;
      const z = node.simulationZ * SPATIAL_SCALE.mapZPerSimulationY;
      const pr = projectToSection(this.frame, x, z);
      if (pr.s < sMin - 10 || pr.s > sMax + 10) continue;
      const cx = this.sToPx(pr.s);
      const cy = this.depthToPy(node.depth);
      ctx.globalAlpha = this.lateralAlpha(pr.lat);
      ctx.beginPath();
      ctx.moveTo(cx, cy - 5);
      ctx.lineTo(cx + 4, cy);
      ctx.lineTo(cx, cy + 5);
      ctx.lineTo(cx - 4, cy);
      ctx.closePath();
      ctx.fillStyle = WAYPOINT_COLOUR;
      ctx.strokeStyle = WAYPOINT_COLOUR;
      if (node.identified) {
        ctx.fill();
        ctx.fillText(`${node.resourceType.toLowerCase()} ${Math.round(node.resourceValue)}`, cx, cy + 16);
      } else {
        ctx.stroke();
      }
    }
    ctx.restore();
  }

  // ---- targets ------------------------------------------------------------

  private drawMissionTarget(ctx: CanvasRenderingContext2D): void {
    const target = this.missionTarget;
    if (!target) return;
    this.drawMarkShape(ctx, { x: target.x, z: target.z, depth: target.depth }, WAYPOINT_COLOUR, 'MISSION', true);
  }

  private drawSelection(ctx: CanvasRenderingContext2D): void {
    const marker = this.dragTarget === 'marker' && this.dragMark ? this.dragMark : this.link.getMarker();
    const waypoint = this.dragTarget === 'waypoint' && this.dragMark ? this.dragMark : this.link.getWaypoint();
    if (marker) {
      this.drawMarkShape(ctx, marker, MARKER_COLOUR, 'MARKER', false);
      // Dashed line from the sub to the marker, like the plan view
      const pr = projectToSection(this.frame, marker.x, marker.z);
      if (marker.depth !== undefined) {
        ctx.save();
        ctx.setLineDash([4, 4]);
        ctx.strokeStyle = 'rgba(224,96,96,0.5)';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(this.sToPx(0), this.depthToPy(this.subDepth));
        ctx.lineTo(this.sToPx(pr.s), this.depthToPy(marker.depth));
        ctx.stroke();
        ctx.restore();
      }
    }
    if (waypoint) this.drawMarkShape(ctx, waypoint, SELECT_COLOUR, 'WAYPOINT', false);
  }

  /** A diamond at (s, depth), or - when the mark has no depth - a vertical dashed line at s. Off-screen marks pin an arrow to the edge. */
  private drawMarkShape(ctx: CanvasRenderingContext2D, mark: Mark, colour: string, label: string, edgeArrows: boolean): void {
    this.animating = true;
    const pr = projectToSection(this.frame, mark.x, mark.z);
    const cx = this.sToPx(pr.s);
    const alpha = this.lateralAlpha(pr.lat);
    const W = this.cssW;
    const H = this.cssH;
    const pulse = 0.75 + 0.25 * Math.sin(performance.now() / 500);
    ctx.save();
    ctx.globalAlpha = Math.max(alpha, 0.35);
    const distance = Math.hypot(mark.x - this.subX, mark.z - this.subZ);
    if (mark.depth === undefined) {
      ctx.setLineDash([6, 5]);
      ctx.strokeStyle = colour;
      ctx.lineWidth = 1.5;
      if (cx >= AXIS_GUTTER_PX && cx <= W) {
        ctx.beginPath();
        ctx.moveTo(cx, 0);
        ctx.lineTo(cx, H);
        ctx.stroke();
        ctx.setLineDash([]);
        ctx.fillStyle = colour;
        ctx.font = '9px monospace';
        ctx.textAlign = 'center';
        ctx.fillText(`${label} ${Math.round(distance)} m`, cx, 24);
        ctx.fillText('any depth', cx, 35);
      } else if (edgeArrows) {
        ctx.globalAlpha = 1;
        this.drawEdgeArrow(ctx, cx, H / 2, colour, `${label} ${Math.round(distance)} m`);
      }
      ctx.restore();
      return;
    }
    const cy = this.depthToPy(mark.depth);
    const margin = 22;
    if (cx < AXIS_GUTTER_PX + margin / 2 || cx > W - margin || cy < margin || cy > H - margin) {
      ctx.globalAlpha = 1;
      if (edgeArrows || label === 'WAYPOINT') this.drawEdgeArrow(ctx, cx, cy, colour, `${label} ${Math.round(distance)} m ${this.deltaText(mark.depth)}`);
      ctx.restore();
      return;
    }
    const s = 8 * pulse;
    const glow = ctx.createRadialGradient(cx, cy, s * 0.5, cx, cy, s * 2.5);
    glow.addColorStop(0, `${colour}66`);
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(cx, cy, s * 2.5, 0, Math.PI * 2);
    ctx.fill();
    ctx.beginPath();
    ctx.moveTo(cx, cy - s);
    ctx.lineTo(cx + s, cy);
    ctx.lineTo(cx, cy + s);
    ctx.lineTo(cx - s, cy);
    ctx.closePath();
    ctx.fillStyle = colour;
    ctx.fill();
    ctx.strokeStyle = '#fff4e0';
    ctx.lineWidth = 1;
    ctx.stroke();
    ctx.fillStyle = colour;
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    ctx.fillText(`${label} ${Math.round(mark.depth)} m`, cx, cy + s + 12);
    ctx.fillText(`${Math.round(distance)} m ${this.deltaText(mark.depth)}`, cx, cy + s + 23);
    ctx.restore();
  }

  private deltaText(depth: number): string {
    const delta = depth - this.subDepth;
    if (Math.abs(delta) < 3) return '=';
    return `${delta > 0 ? '▼' : '▲'}${Math.round(Math.abs(delta))}`;
  }

  private drawEdgeArrow(ctx: CanvasRenderingContext2D, tx: number, ty: number, colour: string, text: string): void {
    const W = this.cssW;
    const H = this.cssH;
    const margin = 24;
    const dx = tx - W / 2;
    const dy = ty - H / 2;
    const k = Math.min((W / 2 - margin) / Math.max(Math.abs(dx), 1e-6), (H / 2 - margin) / Math.max(Math.abs(dy), 1e-6));
    const ex = W / 2 + dx * k;
    const ey = H / 2 + dy * k;
    ctx.save();
    ctx.translate(ex, ey);
    ctx.rotate(Math.atan2(dy, dx));
    ctx.beginPath();
    ctx.moveTo(13, 0);
    ctx.lineTo(-8, -9);
    ctx.lineTo(-3, 0);
    ctx.lineTo(-8, 9);
    ctx.closePath();
    ctx.fillStyle = colour;
    ctx.fill();
    ctx.restore();
    ctx.fillStyle = colour;
    ctx.font = '10px monospace';
    ctx.textAlign = ex > W / 2 ? 'right' : 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, ex + (ex > W / 2 ? -18 : 18), clamp(ey, 14, H - 14));
    ctx.textBaseline = 'alphabetic';
  }

  // ---- submarine & HUD ----------------------------------------------------

  private drawSubmarine(ctx: CanvasRenderingContext2D): void {
    const cx = this.sToPx(0);
    const cy = this.depthToPy(this.subDepth);
    const facing = subFacingSign(this.frame, this.subYaw);
    const pitchRad = (this.subPitch * Math.PI) / 180;
    ctx.save();
    ctx.translate(cx, cy);
    const glow = ctx.createRadialGradient(0, 0, 3, 0, 0, 22);
    glow.addColorStop(0, 'rgba(0,255,224,0.28)');
    glow.addColorStop(1, 'transparent');
    ctx.fillStyle = glow;
    ctx.beginPath();
    ctx.arc(0, 0, 22, 0, Math.PI * 2);
    ctx.fill();
    ctx.scale(facing, 1);
    ctx.rotate(-pitchRad);
    // Hull, conning tower, bow marker (nose = +x)
    ctx.fillStyle = SUB_COLOUR;
    ctx.beginPath();
    ctx.ellipse(0, 0, 15, 5.5, 0, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillRect(-3, -9, 7, 5);
    ctx.fillStyle = '#04201c';
    ctx.beginPath();
    ctx.arc(10, 0, 1.8, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  private drawAxes(ctx: CanvasRenderingContext2D, W: number, H: number, depthMin: number, depthMax: number): void {
    // Depth gutter (left)
    ctx.fillStyle = 'rgba(5,8,16,0.82)';
    ctx.fillRect(0, 0, AXIS_GUTTER_PX, H);
    ctx.strokeStyle = 'rgba(111,152,166,0.35)';
    ctx.lineWidth = 1;
    ctx.beginPath();
    ctx.moveTo(AXIS_GUTTER_PX + 0.5, 0);
    ctx.lineTo(AXIS_GUTTER_PX + 0.5, H);
    ctx.stroke();
    const step = niceTickStep(this.scale);
    ctx.font = '10px monospace';
    ctx.textAlign = 'right';
    ctx.fillStyle = AXIS_COLOUR;
    for (let d = Math.ceil(depthMin / step) * step; d <= depthMax; d += step) {
      const y = this.depthToPy(d);
      if (y < 8 || y > H - 12) continue;
      ctx.fillText(`${Math.round(d)}`, AXIS_GUTTER_PX - 6, y + 3);
      ctx.fillRect(AXIS_GUTTER_PX - 3, Math.round(y), 3, 1);
    }
    // Current depth caret
    const sy = this.depthToPy(this.subDepth);
    if (sy > 6 && sy < H - 6) {
      ctx.fillStyle = SUB_COLOUR;
      ctx.beginPath();
      ctx.moveTo(AXIS_GUTTER_PX, sy);
      ctx.lineTo(AXIS_GUTTER_PX - 7, sy - 5);
      ctx.lineTo(AXIS_GUTTER_PX - 7, sy + 5);
      ctx.closePath();
      ctx.fill();
    }
    ctx.fillStyle = AXIS_COLOUR;
    ctx.font = '8px monospace';
    ctx.textAlign = 'left';
    ctx.fillText('DEPTH m', 3, H - 6);

    // Horizontal axis label (bottom) naming what the section runs along
    const names = this.axisEndNames();
    ctx.font = 'bold 10px monospace';
    ctx.fillStyle = AXIS_COLOUR;
    ctx.textAlign = 'left';
    ctx.fillText(`◄ ${names.left}`, AXIS_GUTTER_PX + 8, H - 6);
    ctx.textAlign = 'right';
    ctx.fillText(`${names.right} ►`, W - 8, H - 6);
    // Distance ticks relative to the sub
    const tickStep = niceTickStep(this.scale, 80);
    ctx.font = '9px monospace';
    ctx.textAlign = 'center';
    for (let s = Math.ceil(this.pxToS(AXIS_GUTTER_PX + 30) / tickStep) * tickStep; s <= this.pxToS(W - 30); s += tickStep) {
      if (Math.abs(s) < 1e-6) continue;
      ctx.fillText(`${s > 0 ? '+' : ''}${Math.round(s)}`, this.sToPx(s), H - 18);
    }
  }

  private axisEndNames(): { left: string; right: string } {
    if (this.axis === 'ns') return { left: 'S', right: 'N' };
    if (this.axis === 'ew') return { left: 'W', right: 'E' };
    const hdg = Math.round(((this.subYaw % 360) + 360) % 360);
    return { left: 'AFT', right: `FWD ${String(hdg).padStart(3, '0')}°` };
  }

  private drawReadout(ctx: CanvasRenderingContext2D, _W: number): void {
    const map = this.activeMap;
    const lines = [`DEPTH ${Math.round(this.subDepth)} m  PITCH ${this.subPitch >= 0 ? '+' : ''}${this.subPitch.toFixed(0)}°`];
    if (map) {
      const floor = heightToDepth(sampleMapHeight(map, this.subX, this.subZ), map.baseDepth);
      if (Number.isFinite(floor)) lines.push(`FLOOR ${Math.round(floor - this.subDepth)} m below`);
    }
    ctx.save();
    ctx.font = 'bold 10px monospace';
    ctx.textAlign = 'left';
    const width = Math.max(...lines.map((l) => l.length)) * 6.2 + 12;
    ctx.fillStyle = 'rgba(5,8,16,0.72)';
    ctx.fillRect(AXIS_GUTTER_PX + 6, 6, width, 6 + lines.length * 13);
    ctx.fillStyle = SUB_COLOUR;
    lines.forEach((line, i) => ctx.fillText(line, AXIS_GUTTER_PX + 12, 18 + i * 13));
    ctx.restore();
  }

  // -------------------------------------------------------------------------
  // Pointer / wheel
  // -------------------------------------------------------------------------

  private localPoint(e: PointerEvent | WheelEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  /** Where a mark sits on screen; a depth-less mark is a vertical line (py = null). */
  private markPx(mark: Mark): { px: number; py: number | null } {
    const pr = projectToSection(this.frame, mark.x, mark.z);
    return { px: this.sToPx(pr.s), py: mark.depth === undefined ? null : this.depthToPy(mark.depth) };
  }

  private hitDraggable(x: number, y: number): { target: 'waypoint' | 'marker'; mark: Mark } | null {
    const test = (mark: Mark | null): boolean => {
      if (!mark) return false;
      const p = this.markPx(mark);
      if (p.py === null) return Math.abs(x - p.px) <= GRAB_RADIUS_PX;
      return Math.hypot(x - p.px, y - p.py) <= GRAB_RADIUS_PX;
    };
    const waypoint = this.link.getWaypoint();
    if (waypoint && test(waypoint)) return { target: 'waypoint', mark: waypoint };
    const marker = this.link.getMarker();
    if (marker && test(marker)) return { target: 'marker', mark: marker };
    return null;
  }

  private readonly onPointerDown = (e: PointerEvent): void => {
    this.canvas.setPointerCapture(e.pointerId);
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
    this.downX = e.clientX;
    this.downY = e.clientY;
    this.moved = false;
    this.dragTarget = null;
    this.dragMark = null;
    if (this.pointers.size === 1) {
      const p = this.localPoint(e);
      const hit = this.hitDraggable(p.x, p.y);
      if (hit) {
        this.dragTarget = hit.target;
        const pr = projectToSection(this.frame, hit.mark.x, hit.mark.z);
        this.dragMark = { x: hit.mark.x, z: hit.mark.z, depth: hit.mark.depth ?? this.subDepth, lat: pr.lat };
      }
    }
    this.dirty = true;
  };

  private readonly onPointerMove = (e: PointerEvent): void => {
    const prev = this.pointers.get(e.pointerId);
    if (!prev) return;
    const beyond = Math.hypot(e.clientX - this.downX, e.clientY - this.downY) > CLICK_MOVE_THRESHOLD_PX;

    if (this.pointers.size === 1 && this.dragTarget && this.dragMark) {
      if (beyond) this.moved = true;
      if (this.moved) {
        const p = this.localPoint(e);
        const world = unprojectFromSection(this.frame, this.pxToS(p.x), this.dragMark.lat);
        this.dragMark.x = world.x;
        this.dragMark.z = world.z;
        this.dragMark.depth = clamp(this.pyToDepth(p.y), 0, this.deepestMeters);
        // The plan view's marker follows live while dragging in this view.
        if (this.dragTarget === 'marker') this.link.setMarker({ x: world.x, z: world.z, depth: this.dragMark.depth });
        this.dirty = true;
      }
    } else if (this.pointers.size === 1) {
      this.panS -= (e.clientX - prev.x) / this.scale;
      this.panDepth -= (e.clientY - prev.y) / this.scale;
      this.panDepth = clamp(this.panDepth, -40, this.deepestMeters + 60);
      this.hasPanned = true;
      this.dirty = true;
    } else if (this.pointers.size === 2) {
      const other = [...this.pointers.entries()].find(([id]) => id !== e.pointerId)?.[1];
      if (other) {
        const dist = Math.hypot(e.clientX - other.x, e.clientY - other.y);
        if (this.lastPinchDist > 0) {
          this.zoomAt(dist / this.lastPinchDist, (e.clientX + other.x) / 2, (e.clientY + other.y) / 2);
        }
        this.lastPinchDist = dist;
        this.hasPanned = true;
      }
    }
    if (beyond) this.moved = true;
    this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
  };

  private readonly onPointerUp = (e: PointerEvent): void => {
    const wasSingle = this.pointers.size === 1;
    this.pointers.delete(e.pointerId);
    this.lastPinchDist = 0;
    this.dirty = true;

    if (this.dragTarget) {
      const target = this.dragTarget;
      const mark = this.dragMark;
      this.dragTarget = null;
      this.dragMark = null;
      if (this.moved && mark) {
        if (target === 'waypoint') this.link.moveWaypoint(mark.x, mark.z, mark.depth);
        return;
      }
    }
    if (!wasSingle || this.moved || e.type === 'pointercancel') return;

    const p = this.localPoint(e);
    if (p.x < AXIS_GUTTER_PX) return;

    // Tapping a revealed room selects it (marker snaps to its centre and depth).
    const baseDepth = this.baseDepth();
    let best: NodeEntry | null = null;
    let bestDist = Infinity;
    for (const entry of this.nodeEntries) {
      if (!this.revealedNodeIds.has(`${entry.tileId}:${entry.node.id}`)) continue;
      const pr = projectToSection(this.frame, entry.node.x, entry.node.z);
      const cx = this.sToPx(pr.s);
      const cy = this.depthToPy(localYToDepth(entry.node.y, 0, baseDepth));
      const span = nodeSectionSpan(entry.node, this.frame);
      const rx = Math.max((span ? (span.s1 - span.s0) / 2 : 0) * this.scale, 6) + 8;
      const ry = Math.max(nodeHalfY(entry.node) * this.scale, 6) + 8;
      const nx = (p.x - cx) / rx;
      const ny = (p.y - cy) / ry;
      const d = nx * nx + ny * ny;
      if (d <= 1 && d < bestDist) { best = entry; bestDist = d; }
    }
    if (best) {
      this.link.setMarker({ x: best.node.x, z: best.node.z, depth: Math.max(0, localYToDepth(best.node.y, 0, baseDepth)) });
      return;
    }

    // Tapping the existing marker removes it
    const marker = this.link.getMarker();
    if (marker) {
      const mp = this.markPx(marker);
      if (mp.py !== null && Math.hypot(p.x - mp.px, p.y - mp.py) < 20) {
        this.link.setMarker(null);
        return;
      }
    }

    // Otherwise drop a marker on the section plane at the tapped distance and depth.
    const world = unprojectFromSection(this.frame, this.pxToS(p.x), 0);
    this.link.setMarker({ x: world.x, z: world.z, depth: clamp(this.pyToDepth(p.y), 0, this.deepestMeters) });
  };

  private readonly onWheel = (e: WheelEvent): void => {
    e.preventDefault();
    this.zoomAt(e.deltaY < 0 ? 1.12 : 1 / 1.12, e.clientX, e.clientY);
    this.hasPanned = true;
  };

  private zoomAt(factor: number, clientX: number, clientY: number): void {
    const rect = this.canvas.getBoundingClientRect();
    const px = clientX - rect.left;
    const py = clientY - rect.top;
    const s = this.pxToS(px);
    const d = this.pyToDepth(py);
    this.scale = clamp(this.scale * factor, MIN_SCALE, MAX_SCALE);
    this.panS = s - (px - this.cssW / 2) / this.scale;
    this.panDepth = clamp(d - (py - this.cssH / 2) / this.scale, -40, this.deepestMeters + 60);
    this.dirty = true;
  }
}
